/* v2.94 · Shared React Flow primitives for Studies v2 graphical
   editors. Every study-specific SLD (Load Flow, Short-Circuit,
   Motor Starting) reuses these building blocks so the visual
   language stays consistent. */
import { Handle, Position, MarkerType } from "@xyflow/react";
import {
  Lightning, PlugsConnected, ArrowsClockwise,
  Fan, Circuitry, WarningOctagon,
  Cube, SignIn, SignOut, Path, Plugs,
  Engine, Broadcast,
} from "@phosphor-icons/react";

const BASE = "border bg-[#0A0C10] px-3 py-2 min-w-[130px] font-mono text-xs shadow-lg";
const HANDLE = "!w-2 !h-2 !border-2";

/* Card node — 1 target on left, 1 source on right. Every study uses
   the same footprint so users learn the interaction once. */
export function BusNode({ data, selected, colour, badge, icon: Icon, sub }) {
  return (
    <div className={`${BASE} ${selected ? "border-[#00E5FF]" : "border-white/20"}`}
      style={{ boxShadow: `0 0 0 1px ${colour}30, 0 4px 12px ${colour}20` }}>
      <Handle type="target" position={Position.Left} className={`${HANDLE} !bg-white/40 !border-black`} />
      <Handle type="source" position={Position.Right} className={`${HANDLE} !bg-white/40 !border-black`} />
      <div className="flex items-center gap-1.5" style={{ color: colour }}>
        <Icon size={12} weight="fill" />
        <span className="uppercase text-[10px] tracking-wider">{badge}</span>
      </div>
      <div className="text-white text-sm font-bold mt-0.5">
        {data.id || data.tag || data.name}
      </div>
      {sub && <div className="text-gray-400 text-[10px]">{sub(data)}</div>}
    </div>
  );
}

/* Standard cyan edge — used by every study for connections. */
export function edgeStyle(label) {
  return {
    animated: false,
    style: { stroke: "#00E5FF", strokeWidth: 2 },
    labelBgStyle: { fill: "#0A0C10", stroke: "#00E5FF44", strokeWidth: 1 },
    labelStyle: { fill: "#00E5FF", fontSize: 10, fontFamily: "monospace" },
    labelBgPadding: [4, 2],
    markerEnd: { type: MarkerType.ArrowClosed, color: "#00E5FF", width: 12, height: 12 },
    ...(label ? { label } : {}),
  };
}

/* Colour + icon lookup by node subtype. Extend here when adding a
   new node type instead of scattering colours across files. */
export const NODE_STYLE = {
  SLACK:       { colour: "#FFCC00", badge: "slack",       icon: Lightning },
  PQ:          { colour: "#00E5FF", badge: "pq",          icon: PlugsConnected },
  PV:          { colour: "#7CFF6B", badge: "pv",          icon: ArrowsClockwise },
  UTILITY:     { colour: "#FFCC00", badge: "utility",     icon: Lightning },
  TRANSFORMER: { colour: "#B0A0FF", badge: "transformer", icon: Circuitry },
  FAULT_BUS:   { colour: "#FF3B30", badge: "fault bus",   icon: WarningOctagon },
  MOTOR:       { colour: "#00E5FF", badge: "motor",       icon: Fan },
  GENERATOR:   { colour: "#7CFF6B", badge: "generator",   icon: ArrowsClockwise },
  SYSTEM:      { colour: "#FFCC00", badge: "system",      icon: Lightning },
  // v3.2 · Reliability RBD
  BLOCK:       { colour: "#00E5FF", badge: "block",       icon: Cube },
  SOURCE:      { colour: "#7CFF6B", badge: "source",      icon: SignIn },
  SINK:        { colour: "#FFCC00", badge: "sink",        icon: SignOut },
  // v3.2 · Cable Ampacity raceway cross-section
  RACEWAY:     { colour: "#B0A0FF", badge: "raceway",     icon: Path },
  CONDUCTOR:   { colour: "#00E5FF", badge: "conductor",   icon: Circuitry },
  LOAD:        { colour: "#FF9F0A", badge: "load",        icon: Plugs },
  // v3.2 · Multi-machine Transient Stability
  MACHINE:     { colour: "#7CFF6B", badge: "machine",     icon: Engine },
  NBUS:        { colour: "#00E5FF", badge: "bus",         icon: Broadcast },
  FAULT:       { colour: "#FF3B30", badge: "fault",       icon: WarningOctagon },
};

/* Common right-hand side panel. Renders an editable form from a
   `fields` schema — [{key, label, type, options?, step?}]. */
export function SidePanel({ title, subtitle, node, edge, fields, onChange, onDelete, onClose }) {
  const target = node ? node.data : edge?.data;
  if (!target) return null;
  const isEdge = !!edge && !node;
  return (
    <div className="absolute z-20 top-14 right-2 w-64 bg-[#12151C] border border-[#00E5FF]/30 p-3 font-mono text-xs shadow-2xl"
         data-testid="sld-side-panel">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="label-tag text-[9px]">{isEdge ? "// edit line" : "// edit bus"}</div>
          <div className="text-white text-sm font-bold mt-0.5">{title}</div>
          {subtitle && <div className="text-gray-500 text-[9px]">{subtitle}</div>}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white"
          data-testid="sld-side-panel-close">×</button>
      </div>
      <div className="space-y-2">
        {fields.map((f) => {
          const val = target[f.key] ?? "";
          const testid = `sld-field-${f.key}`;
          if (f.type === "select") return (
            <label key={f.key} className="block">
              <span className="label-tag text-[9px]">{f.label}</span>
              <select value={val} onChange={(e) => onChange(f.key, e.target.value)}
                className="w-full mt-0.5 bg-black/40 border border-white/10 px-2 py-1 text-[11px]"
                data-testid={testid}>
                {f.options.map((o) => (
                  <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
                ))}
              </select>
            </label>
          );
          return (
            <label key={f.key} className="block">
              <span className="label-tag text-[9px]">{f.label}</span>
              <input
                type={f.type || "text"} step={f.step}
                value={val}
                onChange={(e) => {
                  const v = (f.type === "number")
                    ? (e.target.value === "" ? "" : Number(e.target.value))
                    : e.target.value;
                  onChange(f.key, v);
                }}
                className="w-full mt-0.5 bg-black/40 border border-white/10 px-2 py-1 text-[11px]"
                data-testid={testid} />
            </label>
          );
        })}
      </div>
      {onDelete && (
        <button onClick={onDelete}
          className="mt-3 text-[10px] text-[#FF3B30] hover:underline"
          data-testid="sld-side-panel-delete">
          delete this {isEdge ? "line" : "bus"}
        </button>
      )}
    </div>
  );
}

/* Small floating toolbar rendered at the top of every editor. */
export function Toolbar({ children }) {
  return (
    <div className="absolute z-10 top-2 left-2 right-2 flex flex-wrap items-center gap-1 pointer-events-none">
      <div className="pointer-events-auto flex items-center gap-1 bg-[#12151C]/95 border border-white/10 p-1 flex-wrap">
        {children}
      </div>
      <div className="pointer-events-none ml-auto bg-[#12151C]/95 border border-white/10 px-2 py-1 text-[10px] font-mono text-gray-500">
        click a node/line to edit · drag from handle to connect
      </div>
    </div>
  );
}

export function ToolbarBtn({ colour = "#00E5FF", title, onClick, testid, children }) {
  return (
    <button onClick={onClick}
      className="px-2 py-1 text-[10px] font-mono hover:bg-white/5"
      style={{ color: colour }}
      data-testid={testid} title={title}>
      {children}
    </button>
  );
}

export function ToolbarDivider() {
  return <div className="w-px h-4 bg-white/10 mx-1" />;
}
